import createCachedSelector from "re-reselect";
import * as actions from "./actionCreators";
import { denormalize } from "normalizr";
import schemas from "../../utils/schemas";
import { combineKeys } from "utils/functions";

export const getPartnerTypes = state =>
  state.entities.partnerTypes ? Object.keys(state.entities.partnerTypes).map(i => state.entities.partnerTypes[i]) : [];

export const getPersons = state =>
  state.entities.persons ? Object.keys(state.entities.persons).map(i => state.entities.persons[i]) : [];

export const getBanks = state =>
  state.entities.banks ? Object.keys(state.entities.banks).map(i => state.entities.banks[i]) : [];

export const getPositions = state =>
  state.entities.positions ? Object.keys(state.entities.positions).map(i => state.entities.positions[i]) : [];

export const getContactModes = state =>
  state.entities.contactModes ? Object.keys(state.entities.contactModes).map(i => state.entities.contactModes[i]) : [];

export const getCitizenships = state =>
  state.entities.citizenships ? Object.keys(state.entities.citizenships).map(i => state.entities.citizenships[i]) : [];

export const getGenderTypes = state => {
  const item = state.dictionaries.genderTypes;
  const loaded = item.loaded;
  const content = item && item.loaded ? denormalize(item.ids, schemas.genderTypes, state.entities) : [];

  return { loaded, content };
};
export const getPersonDocumentTypes = state =>
  state.entities.personDocumentTypes
    ? Object.keys(state.entities.personDocumentTypes).map(i => state.entities.personDocumentTypes[i])
    : [];

export const getMaritalStatuses = state =>
  state.entities.maritalStatuses
    ? Object.keys(state.entities.maritalStatuses).map(i => state.entities.maritalStatuses[i])
    : [];

export const getSpecialRelations = state =>
  state.entities.specialRelations
    ? Object.keys(state.entities.specialRelations).map(i => state.entities.specialRelations[i])
    : [];

export const getLegalForms = state =>
  state.entities.legalForms ? Object.keys(state.entities.legalForms).map(i => state.entities.legalForms[i]) : [];

export const getCountries = state => {
  const item = state.dictionaries.countries;
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, schemas.countries, state.entities) : [];

  return { loading, loaded, content };
};

export const getCounties = state =>
  state.entities.counties ? Object.keys(state.entities.counties).map(i => state.entities.counties[i]) : [];

export const getCities = state =>
  state.entities.cities ? Object.keys(state.entities.cities).map(i => state.entities.cities[i]) : [];

export const getAddressTypes = state =>
  state.entities.addressTypes ? Object.keys(state.entities.addressTypes).map(i => state.entities.addressTypes[i]) : [];

export const getAddressFromOCR = (state, partnerId) => {
  const addressFromOCR = state.addresses.addressFromOCR;
  if (!addressFromOCR) return null;
  if (
    Object.keys(addressFromOCR)[0] === partnerId.toString() ||
    Object.keys(addressFromOCR)[0] === state.entities.partners?.[partnerId]?.uniqueId
  )
    return Object.values(addressFromOCR)[0];
  return null;
};

export const getBankAccounts = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.bankAccounts.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.bankAccount], state.entities) : null;

  return { loading, loaded, content };
};

export const getActivityFields = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.activityFields.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.activityFields], state.entities) : null;

  return { loading, loaded, content };
};

export const getActivityFieldsTypes = state =>
  state.entities.activityFieldsTypes
    ? Object.keys(state.entities.activityFieldsTypes).map(i => state.entities.activityFieldsTypes[i])
    : [];

export const getPartnerContactModes = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.partnerContactModes.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.partnerContactMode], state.entities) : null;

  return { loading, loaded, content };
};

export const getContactInfos = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.contactInfos.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.contactInfo], state.entities) : null;

  return { loading, loaded, content };
};

const DEFAULT_DATA = { summaryLoaded: false };
const getSummaryState = (state, id) => denormalize(id, schemas.summary, state.entities);

export const getPartnerActivityFieldsSummary = (state, id) => {
  if (id === null || id === undefined) return DEFAULT_DATA;

  const flags = state.summary.activityFieldsSummary[id];
  if (!flags || !flags.loaded || flags.loading) return DEFAULT_DATA;

  const partnerActivityFieldsSummary = getSummaryState(state, id).activityField;

  return { summaryLoaded: flags.loaded, partnerActivityFieldsSummary };
};

export const getPartnerContactInfoSummary = (state, id) => {
  if (id === null || id === undefined) return DEFAULT_DATA;

  const flags = state.summary.contactInfoSummary[id];
  if (!flags || !flags.loaded || flags.loading) return DEFAULT_DATA;

  const partnerContactInfoSummary = getSummaryState(state, id).contactInfo;

  return { summaryLoaded: flags.loaded, partnerContactInfoSummary };
};

export const getPartnerBankAccountsSummary = (state, id) => {
  if (id === null || id === undefined) return DEFAULT_DATA;

  const flags = state.summary.bankAccountsSummary[id];
  if (!flags || !flags.loaded || flags.loading) return DEFAULT_DATA;

  const partnerBankAccountsSummary = getSummaryState(state, id).bankAccounts;

  return { summaryLoaded: flags.loaded, partnerBankAccountsSummary };
};

export const getAddresses = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: [] };

  const item = state.addresses.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.address], state.entities) : [];

  return { loading, loaded, content };
};

export const getPartnerAddressesSummary = (state, id) => {
  if (id === null || id === undefined) return DEFAULT_DATA;

  const flags = state.summary.partnerAddressesSummary[id];
  if (!flags || !flags.loaded || flags.loading) return DEFAULT_DATA;

  const partnerAddressesSummary = getSummaryState(state, id).partnerAddresses;

  return { summaryLoaded: flags.loaded, partnerAddressesSummary };
};

export const getShareholders = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.shareholders.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.shareholder], state.entities) : null;

  return { loading, loaded, content };
};

export const getCurrencies = state =>
  state.entities.currencies ? Object.keys(state.entities.currencies).map(i => state.entities.currencies[i]) : [];

export const getBankAccountTypes = state =>
  state.entities.bankAccountTypes
    ? Object.keys(state.entities.bankAccountTypes).map(i => state.entities.bankAccountTypes[i])
    : [];

export const getConsents = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.consents.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.consent], state.entities) : null;

  return { loading, loaded, content };
};

export const getProperties = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.properties.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, [schemas.property], state.entities) : null;

  return { loading, loaded, content };
};

export const getPartner = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: true, content: null };

  const item = state.partners.items[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(id, schemas.partner, state.entities) : null;

  return { loading, loaded, content };
};

export const getScoringGrids = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };

  const item = state.scoringGrid.list[id];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content = item && item.loaded ? denormalize(item.ids, schemas.scoringGrid, state.entities) : {};

  return { loading, loaded, content };
};

export const getShareholdersSummary = (state, id) => {
  if (id === null || id === undefined) return DEFAULT_DATA;

  const flags = state.summary.shareholdersSummary[id];
  if (!flags || !flags.loaded || flags.loading) return DEFAULT_DATA;

  const shareholdersSummary = getSummaryState(state, id).shareholders;

  return {
    summaryLoaded: flags.loaded,
    shareholdersSummary: Object.values(shareholdersSummary)
  };
};

export const getDimensionPeriods = state => {
  return state.dictionaries.dimensionPeriods;
};

const getPartnerState = (state, id) => denormalize(id, schemas.partner, state.entities);

export const getDealers = (state, id) => {
  if (id === null || id === undefined) return { loading: false, loaded: false, content: null };
  const flags = state?.partners?.dealers[id];
  if (!flags || !flags.loaded || flags.loading) return { loaded: false };

  const loaded = flags && flags.loaded ? true : false;
  const loading = flags && flags.loading ? true : false;
  const content = getPartnerState(state, id).dealers;
  return { content: content, loaded, loading };
};

const phIds = (filters, page, pageSize, state) => {
  const key = actions.loadPartnerLogsKey(filters, pageSize);
  const partnerLogs = state.partnerLogs.list[key];
  if (!partnerLogs) return [];

  const result = partnerLogs[page] ? partnerLogs[page].ids : [];
  return result ? result : [];
};

const partnerLogs = (filters, page, pageSize, state) => (state.entities.partnerLogs ? state.entities.partnerLogs : []);

export const getPartnerLogsCached = createCachedSelector(phIds, partnerLogs, (ids, partnerLogs) =>
  ids.map(id => partnerLogs[id])
)((filters, page, pageSize, _state) => actions.loadPartnerLogsKey(filters, pageSize));

export const getPartnerLogs = (state, filters, page, pageSize) => {
  const key = actions.loadPartnerLogsKey(filters, pageSize);
  const partnerLogs = state.partnerLogs.list[key];

  const loaded = partnerLogs && partnerLogs[page] ? partnerLogs[page].loaded : false;
  const loading = partnerLogs && partnerLogs[page] ? partnerLogs[page].loading : false;
  const totalCount = partnerLogs && partnerLogs.totalCount ? partnerLogs.totalCount : 0;
  const content = loaded ? getPartnerLogsCached(filters, page, pageSize, state) : [];

  return { loading, loaded, content, totalCount };
};

export const getPartnerLog = (state, partnerLogId) => {
  if (partnerLogId === null || partnerLogId === undefined) return { loading: false, loaded: true, content: "" };

  const item = state.partnerLogs.items[partnerLogId];
  const loaded = item && item.loaded ? true : false;
  const loading = item && item.loading ? true : false;
  const content =
    item && item.loaded ? denormalize(partnerLogId, schemas.partnerLog, state.entities).modifiedValues : "";

  return { loading, loaded, content };
};

const getPartnerDealersState = (state, key) => {
  return denormalize(key, schemas.partnerDealer, state.entities);
};

export const getDealersContactModes = (state, partnerId, dealerId) => {
  if (partnerId === null || partnerId === undefined || dealerId === null || dealerId === undefined) {
    return { loading: false, loaded: false, content: null };
  }

  const key = combineKeys(partnerId, dealerId);
  const flags = state.partners.dealerContactModes[key];
  if (!flags || !flags.loaded || flags.loading) return { loaded: false };

  const loaded = flags && flags.loaded ? true : false;
  const loading = flags && flags.loading ? true : false;

  const content = getPartnerDealersState(state, key).contactModes;
  return { loading, loaded, content };
};

export const getDealersContactInfos = (state, partnerId, dealerId) => {
  if (partnerId === null || partnerId === undefined || dealerId === null || dealerId === undefined) {
    return { loading: false, loaded: false, content: null };
  }

  const key = combineKeys(partnerId, dealerId);
  const flags = state.partners.dealerContactInfos[key];
  if (!flags || !flags.loaded || flags.loading) return { loaded: false };

  const loaded = flags && flags.loaded ? true : false;
  const loading = flags && flags.loading ? true : false;

  const content = getPartnerDealersState(state, key).contactInfos;
  return { loading, loaded, content };
};
